import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachInstruction,
  extractInstruction,
  type Instruction,
  type ItemMode,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/tree-item';

import type { UserStandardTreeNode } from '../../../utils/userStandardsTree';
import { LibraryNodeRow } from '../LibraryNodeRow';

import { getTreeItemData, isTreeItemData } from './libraryDndData';
import { useLibraryDnd } from './LibraryDndProvider';

const INDENT_PER_LEVEL = 22;
const AUTO_EXPAND_MS = 600;

interface LibraryTreeItemProps {
  node: UserStandardTreeNode;
  level: number;
  mode: ItemMode;
  expanded: boolean;
  selected: boolean;
  /** Off on touch/narrow layouts — Pragmatic's HTML5 DnD has no touch support, so we render no
   *  handle and skip drag/drop registration; reordering there goes through MoveToDialog. */
  dragEnabled: boolean;
  onToggleExpand: (id: string) => void;
  onSelect: (node: UserStandardTreeNode) => void;
}

function sameInstruction(a: Instruction | null, b: Instruction | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.type !== b.type) return false;
  if (a.type === 'reparent' && b.type === 'reparent') return a.desiredLevel === b.desiredLevel;
  return true;
}

export function LibraryTreeItem({
  node,
  level,
  mode,
  expanded,
  selected,
  dragEnabled,
  onToggleExpand,
  onSelect,
}: LibraryTreeItemProps) {
  const { getDescendantIds } = useLibraryDnd();
  const rowRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const expandTimer = useRef<number | null>(null);
  const onToggleExpandRef = useRef(onToggleExpand);
  useEffect(() => {
    onToggleExpandRef.current = onToggleExpand;
  });

  const [dragging, setDragging] = useState(false);
  const [instruction, setInstruction] = useState<Instruction | null>(null);

  const hasChildren = node.children.length > 0;

  useEffect(() => {
    if (!dragEnabled) return;
    const element = rowRef.current;
    const dragHandle = handleRef.current;
    if (!element || !dragHandle) return;

    const clearExpandTimer = () => {
      if (expandTimer.current !== null) {
        window.clearTimeout(expandTimer.current);
        expandTimer.current = null;
      }
    };

    // Dwell over a collapsed node while trying to nest in → expand it so deeper drops are reachable.
    const maybeAutoExpand = (next: Instruction | null) => {
      const shouldExpand = !expanded && hasChildren && next?.type === 'make-child';
      if (shouldExpand) {
        if (expandTimer.current === null) {
          expandTimer.current = window.setTimeout(() => {
            expandTimer.current = null;
            onToggleExpandRef.current(node.id);
          }, AUTO_EXPAND_MS);
        }
      } else {
        clearExpandTimer();
      }
    };

    const cleanup = combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => getTreeItemData(node.id),
        onDragStart: () => setDragging(true),
        onDrop: () => setDragging(false),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => isTreeItemData(source.data),
        getData: ({ input, element: target, source }) => {
          const block: Instruction['type'][] = [];
          if (isTreeItemData(source.data)) {
            const draggedId = source.data.nodeId;
            // A node can't be dropped into itself or its own descendants (cycle guard). Any other
            // node — folder or standard — is a valid parent, so no type-based make-child block.
            if (draggedId === node.id || getDescendantIds(draggedId).has(node.id)) {
              block.push('reorder-above', 'reorder-below', 'make-child', 'reparent');
            }
          }
          return attachInstruction(getTreeItemData(node.id), {
            input,
            element: target,
            currentLevel: level,
            indentPerLevel: INDENT_PER_LEVEL,
            mode,
            block,
          });
        },
        onDrag: ({ self }) => {
          const next = extractInstruction(self.data);
          setInstruction((prev) => (sameInstruction(prev, next) ? prev : next));
          maybeAutoExpand(next);
        },
        onDragLeave: () => {
          setInstruction(null);
          clearExpandTimer();
        },
        onDrop: () => {
          setInstruction(null);
          clearExpandTimer();
        },
      })
    );
    // Drop the dwell timer too if the row unmounts (or the effect re-runs) mid-dwell.
    return () => {
      clearExpandTimer();
      cleanup();
    };
  }, [node.id, level, mode, expanded, hasChildren, getDescendantIds, dragEnabled]);

  const handle = dragEnabled ? (
    <Tooltip title="Drag to move">
      <IconButton
        ref={handleRef}
        size="small"
        aria-label={`Move ${node.title}`}
        onClick={(event) => event.stopPropagation()}
        sx={{ color: 'text.disabled', cursor: 'grab', p: 0.25, '&:active': { cursor: 'grabbing' } }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  return (
    <Box
      ref={rowRef}
      data-library-node-id={node.id}
      sx={{ position: 'relative', opacity: dragging ? 0.4 : 1 }}
    >
      <LibraryNodeRow
        node={node}
        depth={level}
        dragHandle={handle}
        expanded={expanded}
        selected={selected}
        onToggleExpand={onToggleExpand}
        onSelect={onSelect}
      />
      {instruction && instruction.type !== 'instruction-blocked' && (
        <DropIndicator instruction={instruction} />
      )}
    </Box>
  );
}

export default LibraryTreeItem;
